/**
 * DRF CSV Parser
 *
 * Parses DRF CSV files entirely in the browser; the file itself is never
 * uploaded. Parsed rows are kept in IndexedDB until the end of the day.
 * When a race is ranked, that race's rows ARE sent to the ranking service,
 * trimmed to the fields it reads (RaceAnalyzer.js, RANK_FIELDS).
 */

// Tracks where DRF's 'A' surface code means "About"-distance turf, not synthetic.
// Verified against the DRF/Equibase corpus (see the surface-mapping comment below for
// how) — every 'A' race on file at these tracks; add a track here only after the same
// verification.
const NO_SYNTHETIC_ABOUT_TRACKS = new Set(['DEL', 'PEN', 'EVD', 'FE', 'LAD', 'MTH', 'RP', 'SA', 'TUP']);

// DRF Column Mappings (complete - same as build_training_data.py)
const COLUMN_MAPPING = {
  // Race info
  'R_RCTrack': 'Race_Track_Code',
  'R_RCDate': 'Race_Date',
  'R_RCRace': 'Race_Number',
  'R_Starters': 'Race_Field_Size',
  'R_TrackName': 'Race_Track_Name',
  'R_RaceState': 'Race_State',
  'R_Division': 'Race_Division',
  'R_RaceBred': 'Race_Bred',
  'R_StateBred': 'Race_State_Bred',
  'R_RaceSex': 'Race_Sex',
  'R_RaceAge': 'Race_Age',
  'R_Class': 'Race_Class',
  'R_Purse': 'Race_Purse',
  'R_HiClaim': 'Race_Hi_Claim_Price',
  'R_LoClaim': 'Race_Lo_Claim_Price',
  'R_Distance': 'Race_Distance',
  'R_DistUnit': 'Race_Dist_Unit',
  'R_Inner': 'Race_Inner_Track',
  'R_Surface': 'Surface_Name',
  'R_RaceType': 'Race_Type',
  'R_GradedRace': 'Race_Graded',
  'R_GradedRaceDesc': 'Race_Graded_Desc',
  'R_TrackRecord': 'Race_Track_Record',
  'R_DayOfWeek': 'Race_Day_Of_Week',
  'R_PostTime': 'Race_Post_Time',
  'R_LongClass': 'Race_Long_Class',
  'R_Conditions': 'Race_Conditions',

  // Betting/Program
  'B_ProgNum': 'Program_Number',
  'B_PostPosition': 'Post_Position',
  'B_Horse': 'Horse_Name_Program',
  'B_Jockey': 'Jockey_Program',
  'B_MLOdds': 'Morning_Line_Odds',

  // Horse basics
  'H_Horse': 'Horse_Name',
  'H_Sex': 'Sex',
  'H_Sire': 'Sire',
  'H_Dam': 'Dam',
  'H_DamSire': 'Dam_Sire',
  'H_Trainer': 'Trainer',
  'H_Jockey': 'Jockey',
  'H_Weight': 'Weight',
  'H_Lasix': 'Lasix',
  'H_Blinker': 'Blinkers',

  // Career stats
  'H_Starts': 'Career_Starts',
  'H_Wins': 'Career_Wins',
  'H_Places': 'Career_Places',
  'H_Shows': 'Career_Shows',
  'H_Earnings': 'Career_Earnings',
  'H_CYStarts': 'Current_Year_Starts',
  'H_CYWins': 'Current_Year_Wins',
  'H_CYEarnings': 'Current_Year_Earnings',
  'H_PYStarts': 'Prior_Year_Starts',
  'H_PYWins': 'Prior_Year_Wins',
  'H_PYEarnings': 'Prior_Year_Earnings',

  // Speed figures - KEEP ORIGINAL DRF NAMES (training script expects these)
  // 'S_BestLifetime': 'Best_Beyer_Lifetime',
  // 'S_BestFastDirt': 'Best_Beyer_Fast_Dirt',
  // 'S_BestTurf': 'Best_Beyer_Turf',
  // 'S_BestWet': 'Best_Beyer_Wet',

  // Other
  'CL_Comment': 'Claiming_Comment',
};

// Add HR_* (Past Performance) mappings
for (let i = 1; i <= 12; i++) {
  COLUMN_MAPPING[`HR_Date_${i}`] = `PP${i}_Date`;
  COLUMN_MAPPING[`HR_Track_${i}`] = `PP${i}_Track`;
  COLUMN_MAPPING[`HR_Distance_${i}`] = `PP${i}_Distance`;
  COLUMN_MAPPING[`HR_Surface_${i}`] = `PP${i}_Surface`;
  COLUMN_MAPPING[`HR_TrkCond_${i}`] = `PP${i}_Track_Condition`;
  COLUMN_MAPPING[`HR_Beyer_${i}`] = `PP${i}_Beyer`;
  COLUMN_MAPPING[`HR_SpeedRating_${i}`] = `PP${i}_Speed_Rating`;
  COLUMN_MAPPING[`HR_FirstCall_${i}`] = `PP${i}_Pos_1st_Call`;
  COLUMN_MAPPING[`HR_SecondCall_${i}`] = `PP${i}_Pos_2nd_Call`;
  COLUMN_MAPPING[`HR_ThirdCall_${i}`] = `PP${i}_Pos_3rd_Call`;
  COLUMN_MAPPING[`HR_StrCall_${i}`] = `PP${i}_Pos_Stretch`;
  COLUMN_MAPPING[`HR_FinalCall_${i}`] = `PP${i}_Pos_Finish`;
  COLUMN_MAPPING[`HR_FirstCallLenAdj_${i}`] = `PP${i}_Len_1st_Call`;
  COLUMN_MAPPING[`HR_SecondCallLenAdj_${i}`] = `PP${i}_Len_2nd_Call`;
  COLUMN_MAPPING[`HR_ThirdCallLenAdj_${i}`] = `PP${i}_Len_3rd_Call`;
  COLUMN_MAPPING[`HR_StrCallLenAdj_${i}`] = `PP${i}_Len_Stretch`;
  COLUMN_MAPPING[`HR_FinalCallLenAdj_${i}`] = `PP${i}_Len_Finish`;
  COLUMN_MAPPING[`HR_FirstFraction_${i}`] = `PP${i}_Frac_1`;
  COLUMN_MAPPING[`HR_SecondFraction_${i}`] = `PP${i}_Frac_2`;
  COLUMN_MAPPING[`HR_ThirdFraction_${i}`] = `PP${i}_Frac_3`;
  COLUMN_MAPPING[`HR_FourthFraction_${i}`] = `PP${i}_Frac_4`;
  COLUMN_MAPPING[`HR_FinalTime_${i}`] = `PP${i}_Final_Time`;
  COLUMN_MAPPING[`HR_Jockey_${i}`] = `PP${i}_Jockey`;
  COLUMN_MAPPING[`HR_Weight_${i}`] = `PP${i}_Weight`;
  COLUMN_MAPPING[`HR_Odds_${i}`] = `PP${i}_Odds`;
  COLUMN_MAPPING[`HR_PostPosition_${i}`] = `PP${i}_Post`;
  COLUMN_MAPPING[`HR_Starters_${i}`] = `PP${i}_Field_Size`;
  COLUMN_MAPPING[`HR_Race_${i}`] = `PP${i}_Race_Number`;
  COLUMN_MAPPING[`HR_Class_${i}`] = `PP${i}_Class`;
  COLUMN_MAPPING[`HR_Purse_${i}`] = `PP${i}_Purse`;
  COLUMN_MAPPING[`HR_ClaimPrice_${i}`] = `PP${i}_Claim_Price`;
  COLUMN_MAPPING[`HR_FirstHorse_${i}`] = `PP${i}_Winner`;
  COLUMN_MAPPING[`HR_SecondHorse_${i}`] = `PP${i}_Second`;
  COLUMN_MAPPING[`HR_ThirdHorse_${i}`] = `PP${i}_Third`;
  COLUMN_MAPPING[`HR_Lasix_${i}`] = `PP${i}_Lasix`;
  COLUMN_MAPPING[`HR_Blinker_${i}`] = `PP${i}_Blinkers`;
  COLUMN_MAPPING[`HR_TrackVariant_${i}`] = `PP${i}_Track_Variant`;
  COLUMN_MAPPING[`HR_Comment_${i}`] = `PP${i}_Comment`;
  COLUMN_MAPPING[`HR_Trainer_${i}`] = `PP${i}_Trainer`;
}

// Add Workout mappings (keep DRF names for V8.2 compatibility)
for (let i = 1; i <= 12; i++) {
  // Keep original DRF names (backend expects these for bullet_rate_last_6)
  COLUMN_MAPPING[`W_Bullet_${i}`] = `W${i}_Bullet`;
  COLUMN_MAPPING[`W_Rank_${i}`] = `W${i}_Rank`;
  COLUMN_MAPPING[`W_RankIn_${i}`] = `W${i}_RankIn`;
  COLUMN_MAPPING[`W_Distance_${i}`] = `W${i}_Distance`;
  COLUMN_MAPPING[`W_WorkTime_${i}`] = `W${i}_WorkTime`;

  // Legacy mappings (if any old code uses these)
  COLUMN_MAPPING[`W_${i}_Date`] = `Workout_${i}_Date`;
  COLUMN_MAPPING[`W_${i}_Track`] = `Workout_${i}_Track`;
  COLUMN_MAPPING[`W_${i}_Time`] = `W${i}_WorkTime`;  // Map to standardized name
  COLUMN_MAPPING[`W_${i}_Surface`] = `Workout_${i}_Surface`;
}

// Trainer-pattern columns need NO mapping: real DRF files already use the
// T_Category_1 / T_Starts_1 / T_WinPct_1 / T_ROI_1 names the backend reads,
// and unmapped columns pass through verbatim. A previous mapping block here
// renamed T_1_Category-style keys that never occur in real exports — do not
// reintroduce it: "fixing" it to match real names would double-map and break
// every trainer feature.

/**
 * Parse date from DRF filename
 * Examples: Op_260215_sf.csv, Mvr260330_sf.csv
 */
function parseDateFromFilename(filename) {
  // Try format with underscore: Op_260215_sf.csv
  let match = filename.match(/_(\d{6})_/);
  if (match) {
    const dateStr = match[1];
    const year = `20${dateStr.substring(0, 2)}`;
    const month = dateStr.substring(2, 4);
    const day = dateStr.substring(4, 6);
    return `${year}-${month}-${day}`;
  }

  // Try format without underscore: Mvr260330_sf.csv
  match = filename.match(/([A-Za-z]+)(\d{6})_/);
  if (match) {
    const dateStr = match[2];
    const year = `20${dateStr.substring(0, 2)}`;
    const month = dateStr.substring(2, 4);
    const day = dateStr.substring(4, 6);
    return `${year}-${month}-${day}`;
  }

  return null;
}

/**
 * Extract track code from filename
 */
function extractTrackCode(filename) {
  // Try format with underscore: Op_260215_sf.csv
  let match = filename.match(/^([A-Za-z]+)_\d{6}/);
  if (match) {
    return match[1].toUpperCase();
  }

  // Try format without underscore: Mvr260330_sf.csv
  match = filename.match(/^([A-Za-z]+)\d{6}/);
  if (match) {
    return match[1].toUpperCase();
  }

  return null;
}

/**
 * Is this race's R_Distance measured in yards rather than furlongs?
 *
 * DRF answers this directly in R_DistUnit ('Y' = yards, blank = furlongs), and
 * that is the only reliable signal — the breed code is NOT enough. Mixed-breed
 * races (R_RaceBred 'M') run at yards at Arapahoe/Los Alamitos/Lone Star but at
 * genuine furlongs at Lethbridge, and the two overlap in the raw number: 5.000
 * is five furlongs at LBG and would be 500 yards elsewhere. Verified against
 * Equibase charts for six such races.
 *
 * The R_RaceBred fallback covers a handful of Quarter Horse cards that carry no
 * R_DistUnit at all; those are yards regardless.
 */
function isDistanceInYards(row) {
  return row['R_DistUnit']?.trim().toUpperCase() === 'Y' ||
         row['R_RaceBred']?.trim() === 'Q';
}

/**
 * Convert DRF distance to readable format
 * (Same logic as 2.0 backend)
 */
function formatDistance(distValue, inYards) {
  const dist = parseFloat(distValue);
  if (isNaN(dist)) return 'Unknown';

  if (inYards) {
    // Sprint distances are encoded as yards/100: 3.3 = 330 yards, 8.7 = 870.
    const yards = Math.round(dist * 100);
    return `${yards} Yards`;
  }

  // Thoroughbred distance conversion
  if (dist >= 8) {
    const wholeMiles = Math.floor(dist / 8);
    const remainingFurlongs = dist % 8;

    if (remainingFurlongs === 0) {
      return wholeMiles === 1 ? '1 Mile' : `${wholeMiles} Miles`;
    }

    // Fraction map
    const fractionMap = {
      0.5: '1/16', 1: '1/8', 1.5: '3/16', 2: '1/4', 2.5: '5/16',
      3: '3/8', 3.5: '7/16', 4: '1/2', 4.5: '9/16', 5: '5/8',
      5.5: '11/16', 6: '3/4', 6.5: '13/16', 7: '7/8', 7.5: '15/16'
    };

    const roundedFurlongs = Math.round(remainingFurlongs * 2) / 2;
    if (fractionMap[roundedFurlongs] && Math.abs(remainingFurlongs - roundedFurlongs) < 0.01) {
      const fraction = fractionMap[roundedFurlongs];
      return wholeMiles === 1 ? `1 ${fraction} Miles` : `${wholeMiles} ${fraction} Miles`;
    } else {
      const yards = Math.round(remainingFurlongs * 220);
      return wholeMiles === 1 ? `1 Mile ${yards} Yards` : `${wholeMiles} Miles ${yards} Yards`;
    }
  } else {
    // Less than 8 furlongs
    const wholeFurlongs = Math.floor(dist);
    const fractionalPart = dist - wholeFurlongs;

    if (fractionalPart < 0.01) {
      return `${wholeFurlongs} Furlong${wholeFurlongs !== 1 ? 's' : ''}`;
    } else if (Math.abs(fractionalPart - 0.5) < 0.01) {
      return `${wholeFurlongs} 1/2 Furlongs`;
    } else {
      return `${dist} Furlongs`;
    }
  }
}

/**
 * Simple CSV line parser (handles quoted fields)
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * Parse DRF CSV file
 *
 * @param {File} csvFile - Browser File object
 * @returns {Promise<Object>} - Parsed race data
 */
export async function parseCSV(csvFile) {
  const filename = csvFile.name;
  const trackCode = extractTrackCode(filename);
  const date = parseDateFromFilename(filename);

  if (!trackCode || !date) {
    throw new Error(`Invalid DRF filename format: ${filename}. Expected format: Track_YYMMDD_sf.csv`);
  }

  // Read file content
  const content = await csvFile.text();
  const lines = content.split('\n');

  if (lines.length < 2) {
    throw new Error('CSV file is empty or invalid');
  }

  // Parse header
  const headers = parseCSVLine(lines[0]);

  // Filter out Reserved_ columns
  const keepCols = headers.filter(h => !h.startsWith('Reserved_'));

  // Parse races
  const races = {};
  let currentRace = null;
  let totalHorses = 0;
  let trackName = null;
  const skippedLines = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseCSVLine(line);
    if (values.length !== headers.length) {
      // A truncated row or stray comma desyncs the column count. Record it so
      // the upload flow can warn the user — a dropped row is a missing horse.
      // The race column sits early in the row, so a truncated row usually
      // still carries it; otherwise it continues the race above it.
      const ri = headers.indexOf('R_RCRace');
      const own = ri >= 0 && ri < values.length ? String(values[ri] || '').trim() : '';
      skippedLines.push({ line: i + 1, race: own || currentRace });
      continue;
    }

    // Create row object
    const row = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] || '';
    });

    // Extract track name from first row
    if (!trackName && row['R_TrackName']) {
      trackName = row['R_TrackName'].trim();
    }

    // Get race number
    const raceNum = row['R_RCRace']?.trim();
    if (raceNum) {
      currentRace = raceNum;
    }

    if (!currentRace) continue;

    if (!races[currentRace]) {
      races[currentRace] = [];
    }

    // Map column names to readable names
    const mappedRow = {};
    keepCols.forEach(col => {
      const newName = COLUMN_MAPPING[col] || col;
      mappedRow[newName] = row[col];
    });

    // Add formatted distance for this race
    if (!mappedRow.formattedDistance && row['R_Distance']) {
      mappedRow.formattedDistance = formatDistance(row['R_Distance'], isDistanceInYards(row));
    }

    // Convert surface code to full name (D → Dirt, T → Turf, X/A/S → Synthetic/All-Weather)
    if (mappedRow.Surface_Name) {
      const surfaceMap = { 'D': 'Dirt', 'T': 'Turf', 'X': 'Synthetic', 'A': 'Synthetic', 'S': 'Synthetic' };
      // None of these tracks races on a synthetic surface (Remington Park's Equitrack
      // main track was replaced with dirt in 1991) — DRF uses 'A' at all of them for
      // "About"-distance turf races (no exact distance pole; Santa Anita's is the
      // downhill turf course), not a real surface. Confirmed against every 'A' race
      // on file for each track: DRF PP cards cross-checked against Equibase result-chart
      // distance lines ("About X On The Turf" / "Originally Scheduled For About X On
      // Turf"), zero exceptions. See NO_SYNTHETIC_ABOUT_TRACKS.
      if (NO_SYNTHETIC_ABOUT_TRACKS.has(trackCode)) {
        surfaceMap['A'] = 'Turf';
      }
      const code = mappedRow.Surface_Name.trim().toUpperCase();
      mappedRow.Surface_Name = surfaceMap[code] || mappedRow.Surface_Name;
    }

    races[currentRace].push(mappedRow);
    totalHorses++;
  }

  if (Object.keys(races).length === 0) {
    throw new Error(
      'No races found in this file. It reads as a CSV, but not as a DRF race card export — please re-download the card from DRF and try again.'
    );
  }

  const warnings = [];
  if (skippedLines.length > 0) {
    const n = skippedLines.length;
    const races = [...new Set(skippedLines.map(x => x.race).filter(Boolean))]
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    const where = races.length ? ` in Race${races.length === 1 ? '' : 's'} ${races.join(', ')}` : '';
    warnings.push(
      `${n} row${n === 1 ? '' : 's'}${where} couldn't be read — any horse${n === 1 ? '' : 's'} on ${n === 1 ? 'that row is' : 'those rows are'} missing from this card.`
    );
  }

  return {
    trackCode,
    trackName: trackName || trackCode,
    date,
    races,
    metadata: {
      totalRaces: Object.keys(races).length,
      totalHorses,
      filename,
      uploadedAt: new Date().toISOString(),
      warnings
    }
  };
}

/**
 * Get TTL until 23:59:59 today (local time)
 */
function getTTLMilliseconds() {
  const now = new Date();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.getTime() - now.getTime();
}

// ============================================================================
// IndexedDB Helper Functions (for large CSV storage)
// ============================================================================

const DB_NAME = 'FurlongFriendDB';
const STORE_NAME = 'racecards';
// Rankings already fetched, per race + scratches + surface, so a reload or a
// second tab doesn't request them again. They expire with their card.
const RANKINGS_STORE = 'rankings';
const DB_VERSION = 2;

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;
      // Let a newer version of the page (another tab) upgrade the database.
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(RANKINGS_STORE)) {
        db.createObjectStore(RANKINGS_STORE, { keyPath: 'key' });
      }
    };
  });
}

// Runs one transaction on the given stores and resolves when it commits.
async function withStores(names, mode, work) {
  const db = await openDB();
  try {
    const transaction = db.transaction(names, mode);
    const result = work(...names.map(n => transaction.objectStore(n)));
    await new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
    return result;
  } finally {
    db.close();
  }
}

/**
 * A ranking saved on this device, or null when there is none or it expired.
 */
export async function getStoredRanking(key) {
  try {
    const box = {};
    await withStores([RANKINGS_STORE], 'readonly', store => {
      const request = store.get(key);
      request.onsuccess = () => { box.value = request.result; };
    });
    const entry = box.value;
    if (!entry || new Date(entry.expiresAt) < new Date()) return null;
    return entry.result;
  } catch {
    return null;
  }
}

/**
 * Save a ranking until the end of the day, like the card it came from.
 */
export async function storeRanking(key, result) {
  const expiresAt = new Date(Date.now() + getTTLMilliseconds()).toISOString();
  try {
    await withStores([RANKINGS_STORE], 'readwrite', store => store.put({ key, result, expiresAt }));
  } catch {
    // Storage blocked or full: the ranking still shows; a reload asks again.
  }
}

/**
 * Forget every saved ranking for one card (a re-issued card replaces it).
 */
export async function deleteCardRankings(cardKey) {
  try {
    await withStores([RANKINGS_STORE], 'readwrite', store => {
      const range = IDBKeyRange.bound(`${cardKey}_`, `${cardKey}_\uffff`);
      store.delete(range);
    });
  } catch {
    // Nothing saved, or storage blocked.
  }
}

/**
 * Remove every card and ranking saved on this device (sign-out, or a
 * different account signing in).
 */
export async function clearAllRaceData() {
  try {
    await withStores([STORE_NAME, RANKINGS_STORE], 'readwrite', (cards, rankings) => {
      cards.clear();
      rankings.clear();
    });
  } catch (error) {
    console.error('IndexedDB clear error:', error);
  }
}

/**
 * Store parsed CSV in IndexedDB (handles large files)
 * Auto-expires at 23:59:59 local time
 */
export async function storeRacecard(parsedData) {
  const { trackCode, trackName, date } = parsedData;
  const key = `${trackCode}_${date}`;
  const expiresAt = new Date(Date.now() + getTTLMilliseconds()).toISOString();

  const dataToStore = {
    key,
    ...parsedData,
    expiresAt
  };

  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(dataToStore);

    await new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });

    db.close();
    // console.log removed for production

    return {
      trackCode,
      trackName,
      date,
      totalRaces: parsedData.metadata.totalRaces,
      expiresAt
    };
  } catch (error) {
    console.error('IndexedDB storage error:', error);
    throw new Error('Failed to save racecard. Please try again.');
  }
}

/**
 * Get stored racecard from IndexedDB
 */
export async function getRacecard(trackCode, date) {
  const key = `${trackCode}_${date}`;

  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    const data = await new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    db.close();

    if (!data) {
      return null;
    }

    // Check if expired
    if (new Date(data.expiresAt) < new Date()) {
      await deleteRacecard(trackCode, date);
      return null;
    }

    return data;
  } catch (error) {
    console.error('IndexedDB read error:', error);
    return null;
  }
}

/**
 * Get specific race from stored racecard
 */
export async function getRace(trackCode, date, raceNumber) {
  const racecard = await getRacecard(trackCode, date);

  if (!racecard) {
    throw new Error(`No racecard found for ${trackCode} on ${date}. Please upload the CSV file.`);
  }

  const raceNumStr = String(raceNumber);

  if (!racecard.races[raceNumStr]) {
    const available = Object.keys(racecard.races).sort((a, b) => parseInt(a) - parseInt(b)).join(', ');
    throw new Error(`Race ${raceNumber} not found. Available races: ${available}`);
  }

  return {
    trackCode,
    date,
    raceNumber,
    horses: racecard.races[raceNumStr],
    metadata: racecard.metadata
  };
}

/**
 * Delete stored racecard from IndexedDB
 */
export async function deleteRacecard(trackCode, date) {
  const key = `${trackCode}_${date}`;

  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.delete(key);

    await new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });

    db.close();
    // console.log removed for production
  } catch (error) {
    console.error('IndexedDB delete error:', error);
  }
}

/**
 * Clear all expired racecards from IndexedDB
 */
/**
 * Validate race date based on subscription type
 * @param {string} raceDate - Date string in format YYYY-MM-DD or MM/DD/YYYY
 * @param {string} productType - 'monthly', 'trial', 'day_pass', or null
 * @returns {object} { valid: boolean, error: string }
 */
export function validateRaceDate(raceDate, productType) {
  try {
    // Parse race date (always in local timezone to avoid UTC issues)
    let raceDateObj;
    if (raceDate.includes('/')) {
      // MM/DD/YYYY format
      const [month, day, year] = raceDate.split('/');
      raceDateObj = new Date(year, month - 1, day);
    } else {
      // YYYY-MM-DD format - parse components to avoid UTC timezone issues
      const [year, month, day] = raceDate.split('-');
      raceDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Get today at midnight (for comparison)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const raceDateAtMidnight = new Date(raceDateObj);
    raceDateAtMidnight.setHours(0, 0, 0, 0);

    // Check if past date
    if (raceDateAtMidnight < today) {
      const formattedDate = raceDateObj.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      return {
        valid: false,
        error: `This racecard is from ${formattedDate}. Only current or upcoming races are supported.`
      };
    }

    // Check if today - always allowed
    if (raceDateAtMidnight.getTime() === today.getTime()) {
      return { valid: true };
    }

    // Future date - BETA TESTING: Allow all future races (re-enable subscription check for production)
    if (raceDateAtMidnight > today) {
      return { valid: true }; // Allow all users to analyze future races during beta

      /* DISABLED FOR BETA - Re-enable for production launch
      if (productType === 'monthly') {
        // Monthly subscribers can analyze future races
        return { valid: true };
      } else {
        // Trial and Day Pass users - only today's races
        return {
          valid: false,
          error: 'Future race analysis is available for monthly subscribers only. Free trial and day pass focus on today\'s races.'
        };
      }
      */
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: 'Invalid race date format.'
    };
  }
}

/**
 * Check if daily cleanup should run (after 2 AM local time)
 * Returns true if cleanup hasn't run today after 2 AM
 */
export function shouldRunDailyCleanup() {
  try {
    const lastCleanup = localStorage.getItem('lastRacecardCleanup');
    const now = new Date();

    // Get 2 AM today in user's local timezone
    const today2AM = new Date(now);
    today2AM.setHours(2, 0, 0, 0);

    // If no cleanup record, run it
    if (!lastCleanup) {
      return true;
    }

    const lastCleanupDate = new Date(lastCleanup);

    // If it's past 2 AM today and last cleanup was before 2 AM today, run it
    if (now >= today2AM && lastCleanupDate < today2AM) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Cleanup check error:', error);
    return false;
  }
}

/**
 * Clear racecards from past days (automatic daily cleanup)
 * Runs at 2 AM local time to remove yesterday's racecards
 */
export async function clearExpiredRacecards() {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const data = cursor.value;

        // Delete if expired OR if race date is before today.
        // Parse data.date as LOCAL midnight (appending T00:00:00 prevents JS from treating
        // YYYY-MM-DD strings as UTC, which would make today's date appear "before today"
        // for any user west of UTC and wipe today's racecards on every page load).
        const raceDateLocal = data.date ? new Date(data.date + 'T00:00:00') : null;
        const shouldDelete = new Date(data.expiresAt) < now ||
                           (raceDateLocal && raceDateLocal < today);

        if (shouldDelete) {
          cursor.delete();
        }
        cursor.continue();
      }
    };

    const rankings = db.transaction([RANKINGS_STORE], 'readwrite').objectStore(RANKINGS_STORE).openCursor();
    rankings.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (new Date(cursor.value.expiresAt) < now) cursor.delete();
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        // console.log removed for production (cleared racecards count)
        // Update last cleanup timestamp
        localStorage.setItem('lastRacecardCleanup', new Date().toISOString());
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });

    db.close();
  } catch (error) {
    console.error('IndexedDB clear error:', error);
  }
}

/**
 * Get all stored racecards (non-expired) from IndexedDB
 * Returns array sorted by date (newest first)
 */
export async function getAllStoredRacecards() {
  const now = new Date();

  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();

    const racecards = [];

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const data = cursor.value;

        // Skip if expired
        if (new Date(data.expiresAt) >= now) {
          racecards.push({
            trackCode: data.trackCode,
            trackName: data.trackName,
            date: data.date,
            totalRaces: data.metadata?.totalRaces || data.totalRaces,
            expiresAt: data.expiresAt,
            key: data.key
          });
        }

        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error);
    });

    db.close();

    // Sort by date (newest first)
    return racecards.sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (error) {
    console.error('IndexedDB getAllStored error:', error);
    return [];
  }
}
